import { useState, useMemo, useEffect, useRef } from 'react';
import { t } from 'utilities/TranslationUtils';
import { computeKickTileStats } from 'pages/PlayerPerformance/playerPerformanceUtils';
import {
  MeasurementMarkerDto,
  SessionOptionDto,
  SessionKindGroupName,
  KindChartCapability,
  KIND_CAPABILITIES,
} from 'types/models/SessionsPageData';
import SessionsPitchChart from '../charts/SessionsPitchChart';
import SessionsGoalChart from '../charts/SessionsGoalChart';
import KickInfoModal from './KickInfoModal';
import MeasurementsTable from './MeasurementsTable';
import KickStatsSummaryRow from './KickStatsSummaryRow';
import { createDwellTimer } from 'utilities/dwellTimer';

export type ViewMode = 'top' | 'goal' | 'table';

const PREVIEW_VIEW_HEIGHT_PX = 560;
const GOAL_VIEW_HEIGHT_PX = 840;
const CLICK_MODAL_SUPPRESS_MS = 260;
const HOVER_EXIT_DELAY_MS = 150;  // Grace period for user to move from chart to modal
const HOVER_SWITCH_DWELL_MS = 350; // With a modal open, another kick must be hovered this long before the modal moves to it


type Props = {
  data: MeasurementMarkerDto[];
  sessions: SessionOptionDto[];
  selectedIds: Set<string>;
  hoveredId: string | null;
  isLoading?: boolean;
  /** Sky-blue if pitch in landing mode (GK), origin otherwise. */
  pitchMode: 'origin' | 'landing';
  onPointHover: (id: string | null) => void;
  onToggleKick: (id: string) => void;
  onDragSelected: (measurementIds: string[]) => void;
  /** Clears the current drag-selection; when omitted, no clear affordance is shown. */
  onClearSelection?: () => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  selectionUiEnabled?: boolean;
  selectionInteractionsEnabled?: boolean;
  showTableViewTab?: boolean;
  /** Omits the table view entirely (the host renders the table elsewhere). */
  hideTableView?: boolean;
  /** Replaces the plain top-view pitch chart (e.g. with a zone pitch whose zones filter the kicks). */
  topViewContent?: React.ReactNode;
  /** Hides the stat tiles under the chart (the host shows them elsewhere). */
  hideSummary?: boolean;
  /** Top view with the pitch turned 90° (goal on the left, kicks travelling right). */
  landscapeTop?: boolean;
  initialView?: ViewMode;
  performanceMode?: boolean;
  allowSessionDetailsFetch?: boolean;
  activeMeasurementId?: string | null;
  onSelectMeasurement?: (id: string) => void;
  onShowVideosForMeasurement?: (id: string) => void;
  onFilterSessionOnly?: (sessionId: string) => void;
  onAddSessionMeasurements?: (sessionId: string, measurementIds: string[]) => void;
  simplifiedKickModal?: boolean;
  /** Per-kick session size for the info popup's "Show session (N Kicks)" button. */
  getSessionKickCount?: (m: MeasurementMarkerDto) => number;
  onShowInTable?: (m: MeasurementMarkerDto) => void;
  onFilterSession?: (m: MeasurementMarkerDto) => void;
  kind?: SessionKindGroupName;
  initialComparisonId?: string | null;
};

const KickLocationsCard = ({
  data, sessions, selectedIds, hoveredId, isLoading = false, pitchMode,
  onPointHover, onToggleKick, onDragSelected, onClearSelection, hasActiveFilters, onClearFilters,
  selectionUiEnabled = true,
  selectionInteractionsEnabled = true,
  showTableViewTab = true,
  hideTableView = false,
  topViewContent,
  hideSummary = false,
  landscapeTop = false,
  initialView = 'top',
  performanceMode = false,
  allowSessionDetailsFetch = false,
  activeMeasurementId = null,
  onSelectMeasurement,
  onShowVideosForMeasurement,
  onFilterSessionOnly,
  onAddSessionMeasurements,
  simplifiedKickModal = false,
  getSessionKickCount,
  onShowInTable,
  onFilterSession,
  kind,
  initialComparisonId,
}: Props): React.ReactElement => {
  const [view, setView] = useState<ViewMode>(initialView);
  const [kickModal, setKickModal] = useState<{ measurement: MeasurementMarkerDto; x: number; y: number } | null>(null);
  const [modalSuppressedUntil, setModalSuppressedUntil] = useState<number>(0);
  const closeModalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringModalRef = useRef(false);
  // Switching an open modal to another kick needs a dwell: a pointer travelling to the modal crosses other dots.
  const switchDwell = useRef(createDwellTimer(HOVER_SWITCH_DWELL_MS)).current;

  const segments: { value: ViewMode; labelKey: string }[] = useMemo(() => {
    const base: { value: ViewMode; labelKey: string }[] = [
      { value: 'top', labelKey: 'sessions.viewTop' },
    ];
    // Include goal view only if this kind supports it (determined by KIND_CAPABILITIES)
    if (!kind || KIND_CAPABILITIES[kind].goalView) {
      base.push({ value: 'goal', labelKey: 'sessions.viewGoal' });
    }
    if (!hideTableView && showTableViewTab) base.push({ value: 'table', labelKey: 'sessions.viewTable' });
    return base;
  }, [showTableViewTab, hideTableView, kind]);

  const effectiveView: ViewMode = segments.some((s) => s.value === view) ? view : segments[0].value;

  // Avoid stale modal/tooling overlays while top view is intentionally blank during loading.
  useEffect(() => {
    if (isLoading && effectiveView === 'top') setKickModal(null);
  }, [isLoading, effectiveView]);

  // Clean up close timer on unmount.
  useEffect(() => {
    return () => {
      if (closeModalTimerRef.current) clearTimeout(closeModalTimerRef.current);
      switchDwell.cancel();
    };
  }, [switchDwell]);

  // Cancel any pending close timer and reset it.
  const cancelCloseTimer = () => {
    if (closeModalTimerRef.current) {
      clearTimeout(closeModalTimerRef.current);
      closeModalTimerRef.current = null;
    }
  };

  // Schedule modal close after hovering away from chart. Called when onPointHover(null) is triggered.
  // If user hovers into the modal before the timer fires, cancelCloseTimer cancels it.
  const scheduleModalClose = () => {
    cancelCloseTimer();
    closeModalTimerRef.current = setTimeout(() => {
      // Only close if we're not hovering the modal (user may have moved from chart → modal).
      if (!isHoveringModalRef.current) {
        setKickModal(null);
      }
      closeModalTimerRef.current = null;
    }, HOVER_EXIT_DELAY_MS);
  };

  // Round 17: hover opens the white info modal. We compose this on top of the parent's existing
  // cross-chart hover linking so both behaviours work together.
  const handleHoverPos = (id: string | null, clientX?: number, clientY?: number) => {
    onPointHover(id);
    if (!selectionInteractionsEnabled) return;
    if (Date.now() < modalSuppressedUntil) return;
    if (!id) {
      // Hovering away from the chart — close modal after delay (unless hovering modal).
      switchDwell.cancel();
      scheduleModalClose();
      return;
    }
    if (clientX === undefined || clientY === undefined) return;
    const m = data.find((x) => x.id === id);
    if (!m) return;
    // If the same kick is already shown, don't keep jittering — only reposition for a new kick.
    if (kickModal && kickModal.measurement.id === id) {
      switchDwell.cancel();
      return;
    }
    // Hovering over a new kick — cancel any pending close.
    cancelCloseTimer();
    if (kickModal) {
      // A modal is open: move it only if the pointer stays on this kick (not when it is just passing over it).
      switchDwell.schedule(() => setKickModal({ measurement: m, x: clientX, y: clientY }));
      return;
    }
    setKickModal({ measurement: m, x: clientX, y: clientY });
  };

  // Click now toggles selection in place (modal already showing from hover); the modal stays
  // open so the user can toggle back without re-triggering hover.
  const handlePointClick = (id: string) => {
    if (!selectionInteractionsEnabled) return;
    setModalSuppressedUntil(Date.now() + CLICK_MODAL_SUPPRESS_MS);
    switchDwell.cancel();
    setKickModal(null);
    onToggleKick(id);
  };

  const displaySelectedIds = selectionUiEnabled ? selectedIds : new Set<string>();

  // When the user has drag-selected kicks, the cells reflect ONLY that subset.
  // Charts always show ALL kicks — selected ones are visually distinct (glow + border styling).
  const selectionActive = selectionUiEnabled && selectedIds.size > 0;
  const chartData = data;  // All kicks visible on charts (filtered data for current view)
  const statsData = useMemo(
    () => {
      if (selectionActive) {
        // Selection active: use only selected kicks
        return data.filter((m) => selectedIds.has(m.id));
      }
      // No selection: stats follow the same filtered set the charts plot, so the numbers stay
      // consistent with the card's filters (favorite / on-target / result / side) and zone scope.
      return data;
    },
    [data, selectedIds, selectionActive],
  );

  // Pre-aggregate via the shared computeKickTileStats so the stat cells here match every other place
  // (zone selector, player-performance tiles) — including the kind-aware on-target (continuous success
  // average for binary kinds). Pure UI aggregation of already-loaded kicks, so it stays client-side.
  const summaryStats = useMemo(() => computeKickTileStats(statsData, kind), [statsData, kind]);

  let titleKey: string;
  let titleLabel: string | null = null;
  if (effectiveView === 'goal') {
    titleKey = 'sessions.goalViewTitle';
  } else if (effectiveView === 'table') {
    titleKey = '';
    titleLabel = '';
  } else {
    titleKey = 'sessions.viewTop';
  }

  const viewHeightPx =
    effectiveView === 'goal'
      ? GOAL_VIEW_HEIGHT_PX
      : PREVIEW_VIEW_HEIGHT_PX;

  // Shared across the pitch / goal / table views so the stat cells are identical everywhere.
  const summaryRow = hideSummary ? null : (
    <KickStatsSummaryRow
      totalKicks={summaryStats.totalKicks}
      onTargetPct={summaryStats.onTargetPct}
      avgBallSpeed={summaryStats.avgBallSpeed}
      avgSpinRate={summaryStats.avgSpinRate}
      avgPullDirection={summaryStats.avgPullDirection}
      avgSpinAxis={summaryStats.avgSpinAxis}
      avgWallCrossingHeight={summaryStats.avgWallCrossingHeight}
      kind={kind}
      selectionActive={selectionActive}
      selectedCount={summaryStats.totalKicks}
      onClearSelection={onClearSelection}
    />
  );

  return (
    <div
      className='sessions-vis-card'
      style={{
        '--sessions-kick-locations-view-height': `${viewHeightPx}px`,
      } as React.CSSProperties}
    >
      <div className='kick-locations-header'>
        {(titleLabel ?? t(titleKey)) && (
          <div className='kick-locations-title'>
            <h3>{titleLabel ?? t(titleKey)}</h3>
            {isLoading && effectiveView === 'top' && (
              <span className='kick-locations-loading-text'>{t('sessions.loadingDataSlow')}</span>
            )}
            {isLoading && <span className='sessions-spinner kick-locations-spinner' aria-label={t('common.loading')} />}
          </div>
        )}
        {segments.length > 1 && (
          <div className='kick-locations-toggle' role='tablist'>
            {segments.map((seg) => (
              <button
                key={seg.value}
                type='button'
                role='tab'
                aria-selected={effectiveView === seg.value}
                className={`kick-locations-toggle-btn${effectiveView === seg.value ? ' kick-locations-toggle-btn--active' : ''}`}
                onClick={() => setView(seg.value)}
              >
                {t(seg.labelKey)}
              </button>
            ))}
          </div>
        )}
      </div>

      {effectiveView === 'top' && (
        <>
          {topViewContent ? (
            <div className='sessions-kick-locations-view sessions-kick-locations-view--zones'>{topViewContent}</div>
          ) : (
          <div className={`sessions-kick-locations-view sessions-kick-locations-view--top${landscapeTop ? ' sessions-kick-locations-view--landscape' : ''}`}>
            <SessionsPitchChart
              data={chartData}
              sessions={sessions}
              selectedIds={displaySelectedIds}
              hoveredId={hoveredId}
              onPointClick={(id) => handlePointClick(id)}
              onPointHover={handleHoverPos}
              onDragSelected={onDragSelected}
              dragSelectionEnabled={selectionInteractionsEnabled && !performanceMode}
              showSelectionState={selectionUiEnabled}
              mode={pitchMode}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={performanceMode ? undefined : onClearFilters}
              performanceMode={performanceMode}
              clearKicks={isLoading}
              landscape={landscapeTop}
            />
          </div>
          )}
          {summaryRow}
        </>
      )}
      {effectiveView === 'goal' && (
        <>
          <div className={`sessions-kick-locations-view${isLoading ? '' : ' sessions-kick-locations-view--goal'}`}>
            {isLoading ? (
              <div className='sessions-kick-locations-loading' aria-busy='true'>
                <span className='sessions-spinner' aria-label={t('common.loading')} />
              </div>
            ) : (
              <SessionsGoalChart
                data={chartData}
                sessions={sessions}
                selectedIds={displaySelectedIds}
                hoveredId={hoveredId}
                onPointClick={(id) => handlePointClick(id)}
                onPointHover={handleHoverPos}
                onDragSelected={onDragSelected}
                dragSelectionEnabled={selectionInteractionsEnabled && !performanceMode}
                showSelectionState={selectionUiEnabled}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={performanceMode ? undefined : onClearFilters}
                performanceMode={performanceMode}
              />
            )}
          </div>
          {summaryRow}
        </>
      )}
      {effectiveView === 'table' && (
        <>
          <div className='sessions-kick-locations-view sessions-kick-locations-view--table'>
            {isLoading ? (
              <div className='sessions-kick-locations-loading' aria-busy='true'>
                <span className='sessions-spinner' aria-label={t('common.loading')} />
              </div>
            ) : (
              <MeasurementsTable
                data={chartData}
                sessions={sessions}
                selectedIds={displaySelectedIds}
                onToggleKick={onToggleKick}
                showSelectionControls={selectionInteractionsEnabled && !performanceMode}
                onFilterSessionOnly={!performanceMode && selectionInteractionsEnabled ? onFilterSessionOnly : undefined}
                onAddSessionMeasurements={!performanceMode && selectionInteractionsEnabled ? onAddSessionMeasurements : undefined}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={performanceMode ? undefined : onClearFilters}
                performanceMode={performanceMode}
                activeMeasurementId={activeMeasurementId}
                onSelectMeasurement={onSelectMeasurement}
                onShowVideosForMeasurement={onShowVideosForMeasurement}
                kind={kind}
                initialComparisonId={initialComparisonId}
              />
            )}
          </div>
          {summaryRow}
        </>
      )}

      {selectionInteractionsEnabled && kickModal && (
        <KickInfoModal
          measurement={kickModal.measurement}
          sessions={sessions}
          position={{ x: kickModal.x, y: kickModal.y }}
          isSelected={selectedIds.has(kickModal.measurement.id)}
          allowSessionDetailsFetch={allowSessionDetailsFetch}
          /* Round 17: keep the modal open after toggling — the button label flips Select↔Deselect
             so the user can toggle back without re-hovering. */
          onToggleSelect={() => { onToggleKick(kickModal.measurement.id); }}
          onClose={() => { switchDwell.cancel(); setKickModal(null); }}
          simplifiedView={simplifiedKickModal}
          onShowInTable={onShowInTable}
          onFilterSession={onFilterSession}
          sessionKickCount={getSessionKickCount?.(kickModal.measurement)}
          onMouseEnter={() => {
            isHoveringModalRef.current = true;
            cancelCloseTimer();
            switchDwell.cancel();
          }}
          onMouseLeave={() => {
            isHoveringModalRef.current = false;
            scheduleModalClose();
          }}
        />
      )}
    </div>
  );
};

// Re-export so callers don't pull from types just to forward the capability prop.
export type { KindChartCapability, SessionKindGroupName };

export default KickLocationsCard;
